import {
  Mesh,
  MeshPhysicalMaterial,
  Object3D,
  SphereGeometry,
  Vector3,
  type Texture,
  type CanvasTexture,
} from 'three';
import { config } from '../config';
import type { BallState, GameProfile } from '../types';
import { makeNumberTexture } from './NumberTexture';

export interface Ball {
  id: number;
  mesh: Mesh<SphereGeometry, MeshPhysicalMaterial>;
  /** Authoritative position; we copy to mesh.position each frame. */
  position: Vector3;
  /** Authoritative velocity (m/s). */
  velocity: Vector3;
  state: BallState;
  radius: number;
  color: string;
  texture: CanvasTexture;
}

export interface BallSystemOptions {
  profile: GameProfile;
  /** World-space chamber center this BallSystem's balls live inside. */
  chamberCenter: Vector3;
  /** Chamber's interior radius (already inset). */
  chamberRadius: number;
  /** Where this BallSystem's mesh tree should hang. Usually the machine group. */
  parent: Object3D;
  /** Mobile mode toggle (smaller textures, no shadows). */
  mobileMode: boolean;
  /** Index of this machine (0 for single-machine, 0–2 for Sicbo). For reproducible RNG variety. */
  machineIndex: number;
}

/**
 * One ball pool inside one chamber. Owns the ball meshes, runs churn physics each frame,
 * and exposes lookups for the reveal animation.
 */
export class BallSystem {
  readonly profile: GameProfile;
  readonly chamberCenter: Vector3;
  readonly chamberRadius: number;
  readonly ballRadius: number;
  readonly machineIndex: number;
  private readonly balls: Map<number, Ball> = new Map();
  private readonly geom: SphereGeometry;
  private readonly mobileMode: boolean;
  /** Effective inside radius for collision (chamber radius - ball radius). */
  private readonly innerRadius: number;
  /** Cached reusable vectors to avoid GC churn in the per-frame loop. */
  private readonly tmpA = new Vector3();
  private readonly tmpB = new Vector3();
  /** When true, run churn physics. When false (e.g., during a reveal), balls hold position
   *  but the lifted/floating/dropping ball still receives its tween updates. */
  private churnEnabled = true;
  /** RNG seed (per machine) so initial scatter looks different across Sicbo dice. */
  private rngState: number;

  constructor(opts: BallSystemOptions) {
    this.profile = opts.profile;
    this.chamberCenter = opts.chamberCenter.clone();
    this.chamberRadius = opts.chamberRadius;
    this.machineIndex = opts.machineIndex;
    this.mobileMode = opts.mobileMode;
    this.rngState = 0x9E3779B9 + opts.machineIndex * 0x6543ABCD;

    // Single radius factor for ALL game profiles — same ball size regardless of pool count.
    this.ballRadius = opts.chamberRadius * config.balls.radiusFactorVsChamber;
    this.innerRadius = opts.chamberRadius - this.ballRadius;

    this.geom = new SphereGeometry(this.ballRadius, 32, 24);

    for (const id of opts.profile.ballIds) {
      const ball = this.createBall(id);
      opts.parent.add(ball.mesh);
      this.balls.set(id, ball);
    }

    this.scatterBalls();
  }

  /** Pseudo-random float [0,1) (mulberry32). */
  private rand(): number {
    let t = (this.rngState += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  private rand2(min: number, max: number): number {
    return min + this.rand() * (max - min);
  }

  private createBall(id: number): Ball {
    const color = this.profile.colorFor(id);
    const tex = makeNumberTexture({
      digit: id,
      color,
      style: this.profile.textureStyle,
      cellWidth: this.mobileMode
        ? config.balls.textureResolutionMobile / 2
        : config.balls.textureResolution / 2,
      cellHeight: this.mobileMode
        ? config.balls.textureResolutionMobile
        : config.balls.textureResolution,
      repeats: config.balls.digitRepeats,
    });

    const mat = new MeshPhysicalMaterial({
      map: tex as Texture,
      roughness: config.balls.materialRoughness,
      metalness: config.balls.materialMetalness,
      clearcoat: config.balls.materialClearcoat,
      clearcoatRoughness: config.balls.materialClearcoatRoughness,
      envMapIntensity: 0.7,
    });

    const mesh = new Mesh(this.geom, mat);
    mesh.castShadow = !this.mobileMode;
    mesh.receiveShadow = !this.mobileMode;
    mesh.name = `ball-${this.profile.id}-${id}`;
    // Slight initial rotation so the digit is visible from the start.
    mesh.rotation.set(0, this.rand2(0, Math.PI * 2), this.rand2(0, Math.PI * 2));

    return {
      id,
      mesh,
      position: new Vector3(),
      velocity: new Vector3(),
      state: 'pool',
      radius: this.ballRadius,
      color,
      texture: tex,
    };
  }

  /** Place all balls at random points within the chamber. */
  scatterBalls(): void {
    // Place balls without overlap using rejection sampling, capped iteration count.
    const placed: Ball[] = [];
    const minSep = this.ballRadius * 2.05;
    for (const ball of this.balls.values()) {
      let attempts = 0;
      let pos: Vector3 | null = null;
      while (attempts++ < 80) {
        const candidate = this.randomPointInSphere(this.innerRadius);
        let ok = true;
        for (const p of placed) {
          if (candidate.distanceTo(p.position) < minSep) {
            ok = false;
            break;
          }
        }
        if (ok) {
          pos = candidate;
          break;
        }
      }
      if (!pos) pos = this.randomPointInSphere(this.innerRadius);
      pos.add(this.chamberCenter);

      ball.position.copy(pos);
      ball.mesh.position.copy(pos);
      ball.velocity.set(
        this.rand2(-0.4, 0.4),
        this.rand2(-0.2, 0.4),
        this.rand2(-0.4, 0.4),
      );
      ball.state = 'pool';
      placed.push(ball);
    }
  }

  private randomPointInSphere(r: number): Vector3 {
    // Uniform-volume point inside a sphere of radius r.
    let x = 0, y = 0, z = 0, n = 2;
    while (n > 1) {
      x = this.rand2(-1, 1);
      y = this.rand2(-1, 1);
      z = this.rand2(-1, 1);
      n = x * x + y * y + z * z;
    }
    const k = Math.cbrt(this.rand()) * r;
    return new Vector3(x, y, z).normalize().multiplyScalar(k);
  }

  /** Pause/resume churn physics. Reveal animation calls this when the chamber should still. */
  setChurnEnabled(enabled: boolean): void {
    this.churnEnabled = enabled;
  }

  getBall(id: number): Ball {
    const ball = this.balls.get(id);
    if (!ball) {
      throw new Error(
        `Ball id ${id} not in profile "${this.profile.id}". Valid: ${this.profile.ballIds.join(', ')}`,
      );
    }
    return ball;
  }

  /** All balls (live read; do not mutate). */
  get allBalls(): Iterable<Ball> {
    return this.balls.values();
  }

  /** Per-frame physics + mesh sync. dt in seconds. */
  update(dt: number): void {
    if (this.churnEnabled) {
      this.simulate(dt);
    }
    // Always sync mesh position/rotation from authoritative state (RevealAnimation
    // writes directly into ball.position / ball.mesh.rotation for non-pool balls).
    for (const ball of this.balls.values()) {
      if (ball.state === 'pool') {
        ball.mesh.position.copy(ball.position);
        // Slight self-spin from velocity to make tumbling believable.
        const v = ball.velocity;
        ball.mesh.rotation.x += v.z * dt * 1.8;
        ball.mesh.rotation.z -= v.x * dt * 1.8;
        ball.mesh.rotation.y += (v.x + v.z) * dt * 0.4;
      }
    }
  }

  private simulate(dt: number): void {
    const pool: Ball[] = [];
    for (const b of this.balls.values()) {
      if (b.state === 'pool') pool.push(b);
    }

    const center = this.chamberCenter;
    const innerR = this.innerRadius;
    const air = config.physics.airForceUp;
    const gravity = config.physics.gravity;
    const jetExtraBelowFactor = config.physics.jetExtraBelowFactor;
    const jetExtraStrength = config.physics.jetExtraStrength;
    const jetColumnRadiusFactor = config.physics.jetColumnRadiusFactor;
    const noiseAmp = config.physics.noiseAmplitude;
    const damping = Math.exp(-config.physics.damping * dt);
    const repulse = config.physics.repulsionStrength;
    const restitution = config.physics.wallRestitution;
    const maxSpeed = config.physics.maxSpeed;
    const centering = config.physics.centeringStrength;
    const minSep = this.ballRadius * 2;
    const minSep2 = minSep * minSep;
    const jetThresholdY = center.y + innerR * jetExtraBelowFactor; // below this Y → extra jet boost

    // 1. Apply air (constant up) + gravity (down) + jet boost in lower zone + big random impulses.
    for (const ball of pool) {
      // Constant uniform airflow upward.
      ball.velocity.y += air * dt;
      // Gravity pulling everything back down.
      ball.velocity.y -= gravity * dt;
      // Concentrated jet from below — affects only balls in the lower part of the chamber,
      // and tapers off the further from the central column they are (like a real air nozzle).
      if (ball.position.y < jetThresholdY) {
        const dx = ball.position.x - center.x;
        const dz = ball.position.z - center.z;
        const horizR2 = dx * dx + dz * dz;
        const jetR = innerR * jetColumnRadiusFactor;
        const horizFalloff = Math.max(0, 1 - horizR2 / (jetR * jetR));
        // Vertical falloff: stronger the lower the ball is.
        const depth = Math.max(0, (jetThresholdY - ball.position.y) / innerR);
        ball.velocity.y += jetExtraStrength * horizFalloff * Math.min(1, depth * 2) * dt;
      }
      // Big random impulses — the dominant cause of visible chaos.
      ball.velocity.x += (this.rand() - 0.5) * 2 * noiseAmp * dt;
      ball.velocity.z += (this.rand() - 0.5) * 2 * noiseAmp * dt;
      ball.velocity.y += (this.rand() - 0.5) * 2 * (noiseAmp * 0.5) * dt;
      // Optional centering pull (currently 0). Keep the hook in case we want to bias mid-chamber later.
      if (centering > 0) {
        this.tmpA.subVectors(center, ball.position);
        const r = this.tmpA.length();
        if (r > 1e-4) {
          this.tmpA.multiplyScalar((centering * r) / innerR);
          ball.velocity.addScaledVector(this.tmpA, dt);
        }
      }
    }

    // 2. Ball-ball repulsion (O(n²) — fine for ≤48 balls per machine).
    for (let i = 0; i < pool.length; i++) {
      const a = pool[i]!;
      for (let j = i + 1; j < pool.length; j++) {
        const b = pool[j]!;
        this.tmpA.subVectors(b.position, a.position);
        const d2 = this.tmpA.lengthSq();
        if (d2 < minSep2 && d2 > 1e-6) {
          const d = Math.sqrt(d2);
          const overlap = minSep - d;
          this.tmpA.divideScalar(d); // normalize
          const push = (repulse * overlap) * dt;
          // Symmetric push.
          a.velocity.addScaledVector(this.tmpA, -push);
          b.velocity.addScaledVector(this.tmpA, push);
        }
      }
    }

    // 3. Integrate, damp, clamp speed, wall-bounce.
    for (const ball of pool) {
      ball.velocity.multiplyScalar(damping);
      const s = ball.velocity.length();
      if (s > maxSpeed) ball.velocity.multiplyScalar(maxSpeed / s);

      ball.position.addScaledVector(ball.velocity, dt);

      this.tmpB.subVectors(ball.position, center);
      const dist = this.tmpB.length();
      if (dist > innerR) {
        // Push back to wall and reflect velocity along normal.
        this.tmpB.divideScalar(dist || 1);
        const overshoot = dist - innerR;
        ball.position.addScaledVector(this.tmpB, -overshoot);
        const vn = ball.velocity.dot(this.tmpB);
        if (vn > 0) {
          ball.velocity.addScaledVector(this.tmpB, -vn * (1 + restitution));
        }
      }
    }
  }

  /** Reset all balls to fresh scattered positions. */
  reset(): void {
    this.scatterBalls();
  }

  dispose(): void {
    for (const ball of this.balls.values()) {
      ball.texture.dispose();
      ball.mesh.material.dispose();
      ball.mesh.parent?.remove(ball.mesh);
    }
    this.balls.clear();
    this.geom.dispose();
  }
}
